import { Center } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { TransactionType } from "@constants/types";
import { TXN_TYPES } from "../../constants/common";

import {
  CLAIM_TITLE_MAPPINGS,
  UNSTAKE_TITLE_MAPPINGS,
} from "../../constants/transactionMessages";

import { getChains } from "../../config";

import ModalDrawer from "@components/ModalDrawer";
import SuccessErrorModalBodyTemplate from "@components/SuccessErrorModalBodyTemplate";
import StakeSuccess from "@components/SuccessErrorModalBodyTemplate/StakeSuccess";
import WrapUnwrapSuccess from "@components/SuccessErrorModalBodyTemplate/WrapUnwrapSuccess";
import {
  POST_DEPOSIT_DEFI_EXPLORE,
  POST_VAULT_DEFI_EXPLORE,
  POST_TRANSACTION_POPUP,
} from "@constants/analytics";
import useDappConfig from "@hooks/useDappConfig";
import { useRouter } from "next/router";
import { trackEvent } from "utils";
// import VaultWithdraw from "@components/SuccessErrorModalBodyTemplate/VaultWitdraw";
import ClaimELModal from "@components/SuccessErrorModalBodyTemplate/ClaimELModal";
import VaultDeposit from "@components/SuccessErrorModalBodyTemplate/VaultDeposit";
import VaultWithdraw from "@components/SuccessErrorModalBodyTemplate/VaultWitdraw";
import { XIcon } from "@phosphor-icons/react";
import Link from "next/link";

interface TransactionModalProps {
  isOpen: boolean;
  hash: string;
  error: string;
  transactionType: string;
  isTxnProcessing: boolean;
  hasUserDenied: boolean;
  daysToWaitForWithdraw?: string;
  closeAlert: () => void;
  handleTxView: () => void;
  chainId: number;
  amount?: string | null;
  hasSecretKeyForClaim?: boolean;
}

let timerId: any = undefined;
// let autoRedirectTimerId: any = undefined;
const GAIN_PAGE_URL = "https://kerneldao.com/kelp/gain/";

const token = process.env.NEXT_PUBLIC_TOKEN || "";
const network = process.env.NEXT_PUBLIC_NETWORK || "0";

const TransactionModal = ({
  isOpen,
  hash,
  error,
  isTxnProcessing,
  hasUserDenied,
  transactionType,
  daysToWaitForWithdraw,
  chainId,
  amount,
  hasSecretKeyForClaim = false,
  closeAlert,
  handleTxView,
}: TransactionModalProps) => {
  const [isTxnTakingMoreTime, setIsTxnTakingMoreTime] = useState(false);
  const router = useRouter();

  const { config } = useDappConfig();

  const chainConfig = useMemo(() => {
    return getChains(network)[0];
  }, [token, network, chainId]);

  // Wrapped handler to track post_transaction_popup events
  const handleTxViewWithTracking = useCallback(() => {
    trackEvent(POST_TRANSACTION_POPUP, {
      txn_hash: hash || null,
      chain: String(chainId) || "",
      option: "View transaction",
    });
    handleTxView();
  }, [hash, chainId, handleTxView]);

  useEffect(() => {
    if (isTxnProcessing && hash) {
      timerId = setTimeout(() => {
        setIsTxnTakingMoreTime(true);
      }, 30000);
    }

    return () => clearTimeout(timerId);
  }, [isTxnProcessing, hash]);

  // useEffect(() => {
  //   if (
  //     isOpen &&
  //     hash &&
  //     !isTxnProcessing &&
  //     !error &&
  //     transactionType === TXN_TYPES.CLAIM_KERNEL_AIRDROPS
  //   ) {
  //     autoRedirectTimerId = setTimeout(() => {
  //       closeAlert();
  //       router.push(GAIN_PAGE_URL);
  //     }, 5000);
  //   }

  //   return () => clearTimeout(autoRedirectTimerId);
  // }, [isOpen, hash, isTxnProcessing, error, transactionType, closeAlert]);

  const getSuccessMessage = useCallback((transactionType: string) => {
    if (
      transactionType === TXN_TYPES.UNSTAKE &&
      UNSTAKE_TITLE_MAPPINGS[token?.toUpperCase()]
    ) {
      return UNSTAKE_TITLE_MAPPINGS[token?.toUpperCase()];
    } else if (
      transactionType === TXN_TYPES.CLAIM &&
      CLAIM_TITLE_MAPPINGS[token?.toUpperCase()]
    ) {
      return CLAIM_TITLE_MAPPINGS[token?.toUpperCase()];
    } else if (transactionType === TXN_TYPES.WRAP) {
      return "Wrap Successful";
    } else if (transactionType === TXN_TYPES.UNWRAP) {
      return "Unwrap Successful";
    }
    return "Transaction Successful";
  }, []);

  const handleStakeSuccess = () => {
    const REDIRECT_LINK = "/defi";
    trackEvent(POST_DEPOSIT_DEFI_EXPLORE, {
      cta_location: "primary",
      redirect_link: REDIRECT_LINK,
    });

    router.push(REDIRECT_LINK);
    closeAlert();
  };

  const handleVaultDepositSuccess = () => {
    const REDIRECT_LINK = "/defi?asset=agETH";
    trackEvent(POST_VAULT_DEFI_EXPLORE, {
      cta_location: "primary",
      redirect_link: REDIRECT_LINK,
    });

    router.push(REDIRECT_LINK);
    closeAlert();
  };

  const renderContent = () => {
    if (error) {
      if (hasUserDenied) {
        return (
          <SuccessErrorModalBodyTemplate
            transactionType={TransactionType.WARNING}
            modalSubTitle="User denied transaction signature. Please try again"
            modalTitle="Transaction denied"
          />
        );
      }
      return (
        <SuccessErrorModalBodyTemplate
          transactionType={TransactionType.ERROR}
          modalTitle="Transaction Failed"
          modalSubTitle={hash ? "" : error}
          primaryBtnTxt={hash ? "View transaction" : ""}
          onSubmitPrimary={handleTxViewWithTracking}
        />
      );
    } else if (hash) {
      if (isTxnProcessing) {
        if (isTxnTakingMoreTime) {
          return (
            <SuccessErrorModalBodyTemplate
              transactionType={TransactionType.WARNING}
              modalTitle="Transaction in-progress"
              modalSubTitle="Your transaction is taking longer than expected"
              primaryBtnTxt={
                chainConfig.blockExplorers?.default.name || "View Transaction"
              }
              onSubmitPrimary={handleTxViewWithTracking}
            />
          );
        } else {
          return (
            <SuccessErrorModalBodyTemplate
              isSpinnerRequired={true}
              modalTitle="Transaction in progress"
            />
          );
        }
      } else {
        // Successful
        if (transactionType === TXN_TYPES.STAKE) {
          return (
            <StakeSuccess
              transactionType={TransactionType.SUCCESS}
              modalTitle={getSuccessMessage(transactionType)}
              modalSubTitle={`${amount} ${config.xtoken.symbol} minted.`}
              primaryBtnTxt="Explore DeFi"
              onSubmitPrimary={handleStakeSuccess}
              onTxView={handleTxViewWithTracking}
            />
          );
        } else if (
          transactionType === TXN_TYPES.WRAP ||
          transactionType === TXN_TYPES.UNWRAP
        ) {
          const isWrap = transactionType === TXN_TYPES.WRAP;
          return (
            <WrapUnwrapSuccess
              transactionType={TransactionType.SUCCESS}
              modalTitle={getSuccessMessage(transactionType)}
              modalSubTitle={`Your ${amount} ${
                isWrap ? "rsETH" : "wrsETH"
              } has been successfully ${isWrap ? "wrapped" : "unwrapped"}.`}
              primaryBtnTxt="Explore DeFi"
              onSubmitPrimary={handleStakeSuccess}
              onTxView={handleTxViewWithTracking}
              isWrap={isWrap}
            />
          );
        } else if (
          transactionType === TXN_TYPES.VAULT_WITHDRAW ||
          transactionType === TXN_TYPES.YIELD_VAULT_WITHDRAW ||
          transactionType === TXN_TYPES.STABLE_VAULT_WITHDRAW ||
          transactionType === TXN_TYPES.CRYPTANIUM_VAULT_WITHDRAW
        ) {
          const isAirdropVault = transactionType === TXN_TYPES.VAULT_WITHDRAW;
          const isStableVault =
            transactionType === TXN_TYPES.STABLE_VAULT_WITHDRAW;
          const isCryptaniumVault =
            transactionType === TXN_TYPES.CRYPTANIUM_VAULT_WITHDRAW;
          return (
            <VaultWithdraw
              transactionType={TransactionType.SUCCESS}
              modalTitle="Withdrawal queued"
              onSubscribe={closeAlert}
              modalSubTitle={
                isStableVault || isCryptaniumVault
                  ? "Your withdrawal has been processed"
                  : "rsETH will be sent to your wallet in 5-6 days"
              }
              isAirdropVault={isAirdropVault}
              isStableVault={isStableVault}
              isCryptaniumVault={isCryptaniumVault}
              extraText="Stay in the Loop! Sign up with your email for the latest updates on what we are building at Kelp"
            />
          );
        } else if (
          transactionType === TXN_TYPES.VAULT_DEPOSIT ||
          transactionType === TXN_TYPES.YIELD_VAULT_DEPOSIT ||
          transactionType === TXN_TYPES.STABLE_VAULT_DEPOSIT ||
          transactionType === TXN_TYPES.CRYPTANIUM_VAULT_DEPOSIT
        ) {
          const isAirdropVault = transactionType === TXN_TYPES.VAULT_DEPOSIT;
          const isStableVault =
            transactionType === TXN_TYPES.STABLE_VAULT_DEPOSIT;
          const isCryptaniumVault =
            transactionType === TXN_TYPES.CRYPTANIUM_VAULT_DEPOSIT;
          return (
            <VaultDeposit
              modalTitle="Deposit Successful"
              modalSubTitle={
                isStableVault || isCryptaniumVault
                  ? "Your deposit has been processed successfully"
                  : ""
              }
              primaryBtnTxt={isAirdropVault ? "Explore agETH DeFi" : undefined}
              onSubmitPrimary={
                isAirdropVault ? handleVaultDepositSuccess : undefined
              }
              onTxView={handleTxViewWithTracking}
              isAirdropVault={isAirdropVault}
              isStableVault={isStableVault}
              isCryptaniumVault={isCryptaniumVault}
            />
          );
        } else if (
          transactionType === TXN_TYPES.CLAIM_EL_POINTS ||
          transactionType === TXN_TYPES.CLAIM_SCROLL_TOKEN
        ) {
          return (
            <ClaimELModal
              transactionType={TransactionType.SUCCESS}
              modalTitle="Claim Successful"
              onSubscribe={closeAlert}
              extraText="Stay in the Loop! Sign up with your email for the latest updates on what we are building at Kelp"
              primaryBtnTxt="View transaction"
              onSubmitPrimary={handleTxViewWithTracking}
            />
          );
        } else if (transactionType === TXN_TYPES.CLAIM_KERNEL_AIRDROPS) {
          if (hasSecretKeyForClaim) {
            // TODO: Add a new modal details for the secret key claim
            return (
              <ClaimELModal
                transactionType={TransactionType.SUCCESS}
                modalTitle={`You claimed ${amount ? amount : ""} $KERNEL`}
                extraText=""
                modalSubTitle="Your claimed $KERNEL tokens are automatically staked. Go to the staking page to view / unstake your position"
                primaryBtnTxt="View staked $KERNEL"
                secondaryBtnTxt="View transaction"
                onSubmitSecondary={handleTxViewWithTracking}
                onSubmitPrimary={() => {
                  trackEvent(POST_TRANSACTION_POPUP, {
                    txn_hash: hash || null,
                    chain: String(chainId) || "",
                    option: "View staked $KERNEL",
                  });
                  try {
                    if (typeof window !== "undefined" && window.open) {
                      window.open("https://kerneldao.com/stake/", "_blank");
                    }
                  } catch (error) {
                    console.error("Failed to open window:", error);
                  }
                }}
                hideGainOpportunities
                transactionDetails={
                  <div>
                    Your $Kernel is claimed. To monitor or withdraw,{" "}
                    <Link href={GAIN_PAGE_URL} target="_blank">
                      click{" "}
                      <span style={{ textDecoration: "underline" }}>here</span>
                    </Link>
                  </div>
                }
              />
            );
          } else {
            return (
              <ClaimELModal
                transactionType={TransactionType.SUCCESS}
                modalTitle={`You claimed & staked ${
                  amount ? amount : ""
                } $KERNEL`}
                extraText=""
                modalSubTitle="Your claimed $KERNEL tokens are automatically staked. Go to the staking page to view / unstake your position"
                primaryBtnTxt="View staked $KERNEL"
                secondaryBtnTxt="View transaction"
                onSubmitSecondary={handleTxViewWithTracking}
                onSubmitPrimary={() => {
                  trackEvent(POST_TRANSACTION_POPUP, {
                    txn_hash: hash || null,
                    chain: String(chainId) || "",
                    option: "View staked $KERNEL",
                  });
                  try {
                    if (typeof window !== "undefined" && window.open) {
                      window.open("https://kerneldao.com/stake/", "_blank");
                    }
                  } catch (error) {
                    console.error("Failed to open window:", error);
                  }
                }}
                hideGainOpportunities
                transactionDetails={
                  <div>
                    Your $Kernel is claimed and staked. To monitor or withdraw,{" "}
                    <Link href={GAIN_PAGE_URL} target="_blank">
                      click{" "}
                      <span style={{ textDecoration: "underline" }}>here</span>
                    </Link>
                  </div>
                }
              />
            );
          }
        } else {
          return (
            <SuccessErrorModalBodyTemplate
              transactionType={TransactionType.SUCCESS}
              modalTitle={getSuccessMessage(transactionType)}
              modalSubTitle={
                transactionType === TXN_TYPES.UNSTAKE && daysToWaitForWithdraw
                  ? `Unstaking will take ${daysToWaitForWithdraw} days. Please check request status in the withdraw section.`
                  : ""
              }
              primaryBtnTxt="View transaction"
              onSubmitPrimary={handleTxViewWithTracking}
            />
          );
        }
      }
    } else {
      return (
        <SuccessErrorModalBodyTemplate
          isSpinnerRequired={true}
          modalTitle="Please check your wallet"
        />
      );
    }
  };

  return (
    <ModalDrawer
      isOpen={isOpen}
      closeAlert={closeAlert}
      closeOnOverlayClick={true}
    >
      <Center
        padding={{
          base: "0",
          md: `${
            transactionType === TXN_TYPES.STAKE ||
            transactionType === TXN_TYPES.UNSTAKE ||
            transactionType === TXN_TYPES.WRAP ||
            transactionType === TXN_TYPES.UNWRAP
              ? "0"
              : "2.5rem 3rem"
          }`,
        }}
        fontWeight="600"
        flexDirection="column"
        width="100%"
        position="relative"
      >
        {/* <CloseIcon onClose={closeAlert} /> */}
        <div className="w-max h-max p-2 rounded-lg border border-solid border-border-default absolute top-4 right-4 hidden md:block cursor-pointer">
          <XIcon
            size={16}
            color="#075A5A"
            weight="regular"
            onClick={closeAlert}
          />
        </div>
        <div className="w-full mt-4">{renderContent()}</div>
      </Center>
    </ModalDrawer>
  );
};

export default TransactionModal;
